import Vue from 'vue';
import VueRouter from 'vue-router';
import Axios from 'axios';
import store from './store';

import Dashboard from './components/dashboard/Index.vue';
import IntroWorkflow from './components/intro/Workflow.vue';
import LienView from './components/lien/View.vue';
import Login from './components/Login.vue';
import PageNotFound from './components/PageNotFound.vue';
import PasswordForgotForm from './modules/public/components/PasswordForgotForm.vue';
import PasswordReset from './components/user/password/ResetPassword.vue';
import TenantView from './components/tenant/View.vue';
import TemplateCustomize from './components/template/Customize.vue';
import Workflow from './components/workflow/Index.vue';
import WorkflowEdit from './components/workflow/Edit.vue';
import WorkflowView from './components/workflow/View.vue';
import TeamView from './components/team/Manage.vue';
import Company from './components/company/Index.vue';
import Companies from './components/companies/Index.vue';
import UserCreate from './components/user/Create.vue';
import UserProfile from './components/user/Profile.vue';
import LocationAdd from './components/location/LocationAdd.vue';
import LienSummary from './components/lien/LienSummary.vue';
import Reports from './components/reports/Reports.vue';
import DocUpload from './components/docUpload/index.vue';
import PageForbidden from './components/PageForbidden.vue';

import { canAccessPath } from './utils/roleUtils';
import { PermissionGroupName, PermissionLevel } from './constants';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    component: () => import(/* webpackChunkName: "default" */'@/layouts/default'),
    children: [
      {
        name: 'dashboard',
        path: '/',
        component: Dashboard,
        meta: {
          title: 'Dashboard',
          permissions: [{ group: PermissionGroupName.DASHBOARD, level: PermissionLevel.READ }]
        }
      },
      {
        name: 'intro-workflow',
        path: '/intro',
        component: IntroWorkflow,
        meta: {
          title: 'Workflow configuration',
          permissions: [{ group: PermissionGroupName.WORKFLOW_CONFIGURATION, level: PermissionLevel.READ }]
        }
      },
      {
        name: 'intro-template',
        path: '/intro/template',
        component: TemplateCustomize,
        meta: {
          title: 'Template customization',
          permissions: [{ group: PermissionGroupName.TEMPLATE, level: PermissionLevel.READ }]
        }
      },
      {
        name: 'lien-view',
        path: '/lien/view/:uuid',
        props: true,
        component: LienView,
        meta: {
          title: 'Lien information',
          permissions: [{ group: PermissionGroupName.LIEN, level: PermissionLevel.READ }]
        }
      },
      {
        name: 'tenant-view',
        path: '/tenant/view/:uuid',
        props: true,
        component: TenantView,
        meta: {
          title: 'Tenant information',
          permissions: [{ group: PermissionGroupName.RENTER, level: PermissionLevel.READ }]
        }
      },
      {
        name: 'team-view',
        path: '/team',
        component: TeamView,
        meta: {
          title: 'Manage your team',
          permissions: [{ group: PermissionGroupName.TEAM, level: PermissionLevel.READ }]
        }
      },
      {
        name: 'template-customize',
        path: '/template',
        component: TemplateCustomize,
        meta: {
          title: 'Customize your notification template',
          permissions: [{ group: PermissionGroupName.TEMPLATE, level: PermissionLevel.MODIFY }]
        }
      },
      {
        name: 'user-profile',
        path: '/user/profile',
        component: UserProfile,
        meta: {
          title: 'Edit your personal information',
          permissions: []
        }
      },
      {
        path: '/workflow',
        component: () => import(/* webpackChunkName: "workflow" */'@/components/workflow/WorkflowContainer'),
        children: [
          {
            name: 'workflow-view',
            path: 'view/:uuid',
            props: true,
            component: WorkflowView,
            meta: {
              title: 'Workflow details',
              permissions: [{ group: PermissionGroupName.WORKFLOW, level: PermissionLevel.READ }]
            }
          },
          {
            name: 'workflow-add',
            path: 'add/:uuid?',
            props: true,
            component: WorkflowEdit,
            meta: {
              title: 'Add a new Workflow',
              permissions: [{ group: PermissionGroupName.WORKFLOW, level: PermissionLevel.CREATE }]
            }
          },
          {
            name: 'workflow-edit',
            path: 'edit/:uuid',
            props: true,
            component: WorkflowEdit,
            meta: {
              title: 'Edit Workflow',
              permissions: [{ group: PermissionGroupName.WORKFLOW, level: PermissionLevel.MODIFY }]
            }
          },
          {
            name: 'workflow-list',
            path: '/',
            component: Workflow,
            meta: {
              title: 'Workflow list',
              permissions: [{ group: PermissionGroupName.WORKFLOW, level: PermissionLevel.READ }]
            }
          }
        ]
      },
      {
        name: 'forbidden',
        path: '/forbidden',
        component: PageForbidden,
        meta: {
          title: 'Permission denied',
          permissions: []
        }
      },
      {
        name: 'company',
        path: '/company',
        component: Company,
        meta: {
          title: 'Company',
          permissions: [{ group: PermissionGroupName.COMPANY }]
        }
      },
      {
        name: 'companies',
        path: '/companies',
        component: Companies,
        meta: {
          title: 'Companies',
          permissions: [{ group: PermissionGroupName.COMPANIES }]
        }
      },
      {
        name: 'location-add',
        path: '/location-add',
        component: LocationAdd,
        meta: {
          title: 'Add Location',
          permissions: [{ group: PermissionGroupName.ADD_LOCATION }]
        }
      },
      {
        name: 'reports',
        path: '/reports',
        component: Reports,
        meta: {
          title: 'Reports',
          permissions: [{ group: PermissionGroupName.REPORTS }]
        }
      },
      {
        name: 'doc-upload',
        path: '/doc-upload',
        component: DocUpload,
        meta: {
          title: 'Document uploads',
          permissions: [{ group: PermissionGroupName.DOC_UPLOAD }]
        }
      }
    ]
  },
  {
    path: '/login',
    component: () => import(/* webpackChunkName: "public" */'@/layouts/public'),
    children: [
      {
        path: '',
        name: 'login',
        component: Login,
        meta: {
          title: 'Login',
          permissions: []
        }
      },
      {
        name: 'password-forgot',
        path: 'forgot',
        component: PasswordForgotForm,
        meta: {
          title: 'Password Lost',
          permissions: []
        }
      },
      {
        name: 'password-reset',
        path: 'reset/:uuid',
        props: true,
        component: PasswordReset,
        meta: {
          title: 'Reset Password',
          permissions: []
        }
      }
    ]
  },
  // @todo redesign this page
  {
    name: 'lien-summary',
    path: '/lien-summary/:uuid',
    props: true,
    component: LienSummary,
    meta: {
      title: 'Lien Summary',
      permissions: []
    }
  },
  // @todo redesign this page
  {
    name: 'user-create',
    path: '/register/:uuid',
    props: true,
    component: UserCreate,
    meta: {
      title: 'Create your account',
      permissions: []
    }
  },
  {
    name: '404',
    path: '*',
    component: PageNotFound,
    meta: {
      title: 'Page not found',
      permissions: []
    }
  }
];

const router = new VueRouter({ mode: 'history', routes });

const visitorRoute = ['login', 'password-forgot', 'password-reset', 'user-create', 'lien-summary'];

// @todo guards!
router.beforeEach((to, from, next) => {
  // <title> tag update
  document.title = to.meta.title || 'Self Storage Solution';

  // set default value to vue, if needed, component will override them when mounted() is called
  // find list and explanation on main.js
  router.app.$root.showNavigation = true;
  router.app.$root.showHeader = visitorRoute.includes(to.name);
  router.app.$root.goBackLink = null;

  const isUserLogged = store.state.accessToken && store.state.expirationDate > (new Date()).getTime();
  const hasUserCompany = store.state.company.uuid || store.state.user.access;
  const userRole = store.state.user.role;
  if (!isUserLogged && hasUserCompany && store.state.refreshToken) { // if remember me checked, we try to refreshtoken
    const data = new FormData();
    data.append('grant_type', 'refresh_token');
    data.append('refresh_token', store.state.refreshToken);
    data.append('client_id', store.state.CLIENT_CREDENTIALS.client_id);
    data.append('client_secret', store.state.CLIENT_CREDENTIALS.client_secret);
    // @todo async-await
    Axios.post(`${store.state.API_URL}user/refresh_token`, data)
      // @todo remove this disabling rule
      // eslint-disable-next-line consistent-return
      .then((response) => {
        if (response.data.status === 'success') {
          store.commit('refreshToken', response);
          next();
        } else {
          store.dispatch('logout', () => { next({ name: 'login' }); });
          return true;
        }
      })
      .catch(() => {
        store.dispatch('logout', () => { next({ name: 'login' }); });
        return true;
      });
  } else if (!isUserLogged || !hasUserCompany) {
    // if user is not logged and tries to go on any page, redirect to login and remove saved data
    // or if user is logged but has not company set, redirect to login
    if (!visitorRoute.includes(to.name)) {
      store.dispatch('logout', () => { next({ name: 'login' }); });
      // router.app.$root.showHeader = false;
    } else {
      next();
    }
  } else if (!canAccessPath(userRole, to.meta?.permissions)) {
    next({ name: 'forbidden' });
  } else if (
    !store.state.accountReady
    && ['login', 'location-add', 'user-profile'].includes(to.name)
    && (!store.state.locationsList || !store.state.locationsList.length)
  ) {
    next({ name: 'location-add' });
  } else {
    // @todo this condition is wrong, because unauthorized user have 0 organizations
    // which means its also < 2
    if (
      (store.state.organizations.length < 2 && to.name === 'login')
      || ['password-forgot', 'password-reset', 'user-create'].includes(to.name)
    ) {
      // specific case : user tries to go on /login but already logged and has only one company, redirect to dashboard .
      // if user is logged and try to go on password reset / forgot -> redirect to dashboard
      // dont add to.name == login here because user still need to switch location using this route
      next({ name: 'dashboard' });
      return;
    }

    next();
  }
});

export default router;
